import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Buffer } from 'buffer';
import { srpLogin, refreshSession, signOutLocal, type SessionResult } from './cognito';
import { loadTokens, saveTokens, clearTokens, type StoredTokens } from './storage';

export interface AuthUser {
  email: string;
  parentId: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  hydrating: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const Ctx = createContext<AuthContextValue | null>(null);

function decodeSub(idToken: string): string {
  const parts = idToken.split('.');
  if (parts.length < 2 || !parts[1]) return '';
  try {
    const json = Buffer.from(parts[1].replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString('utf-8');
    return (JSON.parse(json) as { sub?: string }).sub ?? '';
  } catch { return ''; }
}

function toUser(tokens: StoredTokens): AuthUser {
  return { email: tokens.email, parentId: decodeSub(tokens.idToken) };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [hydrating, setHydrating] = useState(true);
  const tokensRef = useRef<StoredTokens | null>(null);
  // Tracks an in-flight refreshSession call. If a second caller hits
  // getIdToken while a refresh is already running, they await the same
  // promise instead of firing a second Cognito refresh.
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    const stored = loadTokens();
    if (stored) {
      tokensRef.current = stored;
      setUser(toUser(stored));
    }
    setHydrating(false);
  }, []);

  const persist = useCallback((email: string, session: SessionResult) => {
    const tokens: StoredTokens = { ...session, email };
    tokensRef.current = tokens;
    saveTokens(tokens);
    setUser(toUser(tokens));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await srpLogin(email, password);
    persist(email, session);
  }, [persist]);

  const signOut = useCallback(async () => {
    const email = tokensRef.current?.email;
    if (email) signOutLocal(email);
    tokensRef.current = null;
    clearTokens();
    setUser(null);
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    const t = tokensRef.current;
    if (!t) return null;
    if (Date.now() < t.expiresAt - 60_000) return t.idToken;
    // If another caller has already kicked off a refresh, piggy-back on it.
    // Without this guard, Promise.all([api.foo(), api.bar()]) would fire two
    // refresh requests at Cognito and one would silently overwrite the other.
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    refreshPromiseRef.current = (async () => {
      try {
        const session = await refreshSession(t.email, t.refreshToken);
        persist(t.email, session);
        return session.idToken;
      } catch {
        await signOut();
        return null;
      }
    })().finally(() => { refreshPromiseRef.current = null; });
    return refreshPromiseRef.current;
  }, [persist, signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, hydrating, signIn, signOut, getIdToken }),
    [user, hydrating, signIn, signOut, getIdToken]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
